import { If, onMount, watch } from 'cdui-js';
import { CheckBox } from '../../../../components/CheckBox';
import { lastProcess, nextProcess, pState, getApplySaveExtra } from '../../store';
import { busConfigs, initBusConfigs, busConfigsTick } from '../../../../../../stores/bus-config';
import { LastNextBtns } from '../../components/LastNextBtns';
import { saveApply } from '../../../../../../api/open-account';
import i18n from '../../../../../../i18n';
import { isOpenProgressReEdit } from '../../reEditMode';

/** 接口 `other` 分组标题多为简体中文，按文案映射到当前语言 */
function localizeElectronicGroupName(name: string) {
  if (!name) return name;
  const E = i18n.OpenProgress.Info.Electronic;
  if (name === '电子交易服务' || name === E.GroupTrade) return E.GroupTrade;
  if (name === '电子结单/电子函件及双重认证资讯服务' || name === E.GroupStatement) return E.GroupStatement;
  // 兼容繁体源数据
  if (name === '電子交易服務') return E.GroupTrade;
  if (name === '電子結單/電子函件及雙重認證資訊服務') return E.GroupStatement;
  return name;
}

function parseSelectedServiceValues(selected: string) {
  return new Set(
    String(selected || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  );
}

/** 从当前表单列表收集 value → check，供字典刷新时保留未提交的勾选 */
function collectPrevChecks() {
  const prev = new Map<string, boolean>();
  Object.keys(pState.activateEleServicesForm.list || {}).forEach((key) => {
    (pState.activateEleServicesForm.list[key]?.list || []).forEach((i) => {
      if (i?.value != null) prev.set(String(i.value), !!i.check);
    });
  });
  return prev;
}

function resolveServiceCheck(
  value: string,
  selectedSet: Set<string> | null,
  prevChecks: Map<string, boolean>,
) {
  // 1) 已有最终结果（下一步保存 / 服务端回填）→ 严格按 selected
  if (selectedSet) return selectedSet.has(value);
  // 2) 本会话已勾选过但未写入 selected → 保留
  if (prevChecks.has(value)) return prevChecks.get(value)!;
  // 3) 真正首次进入 → 默认全选
  return true;
}

function groupByOtherWithId(list, prevChecks: Map<string, boolean>) {
  const nameToId = new Map();
  let nextId = 1;
  const result = {};
  const selectedRaw = String(pState.activateEleServicesForm.selected || '').trim();
  const selectedSet = selectedRaw ? parseSelectedServiceValues(selectedRaw) : null;
  for (const item of list || []) {
    const name = localizeElectronicGroupName(item.other);
    let id = nameToId.get(name);

    if (id == null) {
      id = nextId++;
      nameToId.set(name, id);
      result[id] = { name, list: [] };
    }

    const check = resolveServiceCheck(String(item.value), selectedSet, prevChecks);
    // 拷贝进 pState，避免字典对象被响应式代理后再嵌套形成环
    result[id].list.push({ ...item, check });
  }
  return result;
}

/** value + check + 分组名；一致则跳过赋值，避免 tick 抖动冲掉未保存勾选 */
function serviceListSignature(grouped) {
  return Object.keys(grouped || {})
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => {
      const group = grouped[key];
      const items = (group?.list || [])
        .map((i) => `${i?.value}:${i?.check ? 1 : 0}`)
        .join(',');
      return `${group?.name ?? ''}|${items}`;
    })
    .join(';');
}

function rebuildList() {
  const prevChecks = collectPrevChecks();
  const next = groupByOtherWithId(busConfigs.electronicService, prevChecks);
  if (serviceListSignature(next) === serviceListSignature(pState.activateEleServicesForm.list)) {
    return;
  }
  pState.activateEleServicesForm.list = next;
}

function flattenServiceList() {
  const list = [];
  Object.keys(pState.activateEleServicesForm.list).forEach((key) => {
    list.push(...(pState.activateEleServicesForm.list[key]?.list || []));
  });
  return list;
}

function getSelectedServiceList() {
  return flattenServiceList().filter((i) => i.check);
}

export const ActivateEleServices = (props) => {
  // 字典非响应式；勿在渲染期写 pState.list（每次新对象会触发再渲染并栈溢出）
  onMount(() => {
    void initBusConfigs()
      .then(() => {
        rebuildList();
      })
      .catch(() => {
        rebuildList();
      });
  });

  /** App Theme 晚于 initBusConfigs resolve（空字典）时重建列表；空字典不覆盖已有勾选 */
  watch(
    () => busConfigsTick.v,
    () => {
      if ((busConfigs.electronicService || []).length > 0) {
        rebuildList();
      }
    },
  );

  return (
    <div class="padding-x-L margin-y-XL">
      <div class="font-s-L font-bold color-D margin-b-L">
        {i18n.OpenProgress.Info.Electronic.Title}
        {props.stepText}
      </div>

      {Object.keys(pState.activateEleServicesForm.list).map((key) => {
        const item = pState.activateEleServicesForm.list[key];
        return (
          <div>
            <div class="margin-b color-D font-bold">{item.name}</div>

            {item.list.map((citem) => {
              return (
                <CheckBox
                  class="padding-b-S"
                  active={citem.check}
                  change={() => {
                    citem.check = !citem.check;
                  }}
                >
                  {citem.label}
                </CheckBox>
              );
            })}
          </div>
        );
      })}

      <If when={!isOpenProgressReEdit(props)}>
        <LastNextBtns
          last={() => {
            lastProcess();
          }}
          next={async () => {
            const selectedList = getSelectedServiceList();
            const electronicServices = selectedList.map((i) => i.value);
            await saveApply({
              electronicServices: electronicServices.toString(),
              progress: 10,
              applyType: 'NEW_APPLY',
              ...getApplySaveExtra(),
            });

            pState.activateEleServicesForm.selected = electronicServices.toString();
            nextProcess();
          }}
        />
      </If>
    </div>
  );
};
